// Story 8.3 (ADO-2012), Tasks 11.3 to 11.6: what a refused line write shows, from the
// store's recorded conflict (cards 01 to 04, D-18). The copy is the designer's, word for word,
// em-dashes included (DV-3, D-26).
//
// The designer's number rule: "Change to M" and "Add it back (M)" use the number the person
// picked, replacing what is there now. The message never says who made the change (D-12).

import type { LineConflict } from './useBasketStore';

/** What the message's action does when tapped, for the host to send through the store. */
export type ConflictIntent =
  | { readonly type: 'set'; readonly quantity: number }
  | { readonly type: 'add'; readonly quantity: number }
  | { readonly type: 'remove' };

export interface ConflictViewAction {
  readonly label: string;
  readonly intent: ConflictIntent;
}

export type ConflictView =
  | { readonly kind: 'none' }
  /** The line is there: a Conflict Message on its row or card. */
  | {
      readonly kind: 'changed';
      readonly message: string;
      readonly action: ConflictViewAction | null;
    }
  /** The line is gone: a Removed Line where it was (card 03). */
  | { readonly kind: 'removed'; readonly message: string; readonly action: ConflictViewAction };

/** Card 02's message, also the scan sheet's (D-20). */
export function changedMessage(currentQuantity: number): string {
  return `Now ${currentQuantity} — this line changed while you were editing.`;
}

/** Card 03's message, also the scan sheet's (D-20). */
export const REMOVED_MESSAGE = 'Removed while you were editing.';

export function conflictView(conflict: LineConflict): ConflictView {
  const { action, requestedQuantity, sawLine, currentQuantity } = conflict;
  if (action === 'review') return reviewView(conflict);

  if (currentQuantity === null) {
    // Both removed the same line (OQ-3a): what the person wanted, and no design covers it.
    if (action === 'remove') return { kind: 'none' };
    return {
      kind: 'removed',
      message: REMOVED_MESSAGE,
      action: {
        label: `Add it back (${requestedQuantity})`,
        intent: { type: 'add', quantity: requestedQuantity },
      },
    };
  }

  if (action === 'add' && !sawLine) {
    return {
      kind: 'changed',
      message: `This was just added — now ${currentQuantity} in your cart.`,
      action: null,
    };
  }

  if (action === 'remove') {
    return {
      kind: 'changed',
      message: `Not removed — this line changed to ${currentQuantity} while you were editing.`,
      action: { label: 'Remove anyway', intent: { type: 'remove' } },
    };
  }

  // A change of quantity (card 02), or an add on top of a line that changed: an add's number
  // is an amount to add, not a quantity to change to, so it offers no action.
  const offersChange = action === 'set' && requestedQuantity !== currentQuantity;
  return {
    kind: 'changed',
    message: changedMessage(currentQuantity),
    action: offersChange
      ? {
          label: `Change to ${requestedQuantity}`,
          intent: { type: 'set', quantity: requestedQuantity },
        }
      : null,
  };
}

/** Story 8.3 (ADO-2012), Task 13.1 (card 09, D-24): a line that changed while the Shopper
 *  reviewed the cart, marked after the send was refused. "Changed from 3 to 4" is the designer's
 *  copy; the removed and added lines are worded after it (D-26). "Add it back" adds the quantity
 *  the Shopper reviewed. */
function reviewView({ requestedQuantity, sawLine, currentQuantity }: LineConflict): ConflictView {
  if (!sawLine) {
    return currentQuantity === null
      ? { kind: 'none' }
      : { kind: 'changed', message: 'Added while you were reviewing.', action: null };
  }
  if (currentQuantity === null) {
    return {
      kind: 'removed',
      message: 'Removed while you were reviewing.',
      action: {
        label: `Add it back (${requestedQuantity})`,
        intent: { type: 'add', quantity: requestedQuantity },
      },
    };
  }
  return {
    kind: 'changed',
    message: `Changed from ${requestedQuantity} to ${currentQuantity} while you were reviewing.`,
    action: null,
  };
}
